import { faChevronDown } from "@jewlr/pro-solid-svg-icons/faChevronDown"
import {
  Box,
  Button,
  Flex,
  FontAwesomeIcon,
  Image,
  Typography,
} from "@jewlr/storybook/core"
import { useHandleOutsideClick } from "@jewlr/storybook/helpers"
import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import React, { useRef, useState } from "react"
import styled from "styled-components"

import useNavigation from "helpers/use-navigation"
import withCountrySwitch from "hoc/with-country-switch"
import countries from "site-data/jewlr/countries"

const StyledList = styled.ul`
  background-color: white;
  border: none;
  list-style: none;
  margin: 0;
  max-width: 150px;
  padding: 6px 0;
  position: absolute;
  right: 0;
  z-index: ${(props) => props.theme.zIndices.submenus};
`

const StyledListItem = styled.li`
  list-style: none;
  margin: 0;
  opacity: 0.5;
  padding: 8px 10px;
  &:focus,
  &:hover,
  &:focus-within {
    opacity: 1;
    outline-offset: -2px;
  }
`

const CountryOption = ({
  closeDropdown,
  country,
  hideCountryName,
  onClick,
}) => {
  const keyboardProps = useNavigation({
    onEscape: closeDropdown,
  })

  return (
    <StyledListItem>
      <Button
        aria-selected="false"
        onClick={onClick}
        role="option"
        variant="text"
        {...keyboardProps}
      >
        <Image
          alt={country.human_country}
          aria-hidden={!hideCountryName}
          height="22px"
          mr={{ tablet: 1 }}
          src={`${
            sl.config.jewlr_asset_url
          }images/icons/flags/${country.geo.toLowerCase()}_rd.svg`}
          width="22px"
        />
        {!hideCountryName && (
          <Typography ml={"6px"} mr={"12px"}>
            {country.human_country}
          </Typography>
        )}
      </Button>
    </StyledListItem>
  )
}

CountryOption.propTypes = {
  closeDropdown: PropTypes.func,
  country: PropTypes.object,
  hideCountryName: PropTypes.bool,
  onClick: PropTypes.func,
}

const CountrySelector = ({ doSwitch, hideCountryName }) => {
  const countrySelectorRef = useRef()
  const [expanded, showOptions] = useState(false)

  useHandleOutsideClick({
    enabled: expanded,
    onOutsideClick: () => showOptions(false),
    ref: countrySelectorRef,
  })

  return (
    <Box aria-label="Change Country" ref={countrySelectorRef}>
      <Button
        alignItems="center"
        aria-expanded={expanded}
        aria-haspopup="listbox"
        aria-label="Change country"
        display="flex"
        flexDirection="row"
        id="mobile-menu-country-selector-button"
        justifyContent="space-between"
        maxWidth="150px"
        onClick={() => showOptions(!expanded)}
        variant="text"
      >
        <Flex alignItems="center" py={0}>
          <Image
            alt={sl.config.site_country_for_analytics}
            aria-hidden={!hideCountryName}
            height="22px"
            mr={{ tablet: 1 }}
            src={`${
              sl.config.jewlr_asset_url
            }images/icons/flags/${sl.config.site_country_for_analytics.toLowerCase()}_rd.svg`}
            width="22px"
          />
          {!hideCountryName && (
            <Typography ml="6px" mr="12px">
              {sl.config.site_country_for_analytics.replace("GB", "UK")}
            </Typography>
          )}
        </Flex>
        {!hideCountryName && (
          <FontAwesomeIcon
            icon={faChevronDown}
            rotation={expanded ? 180 : null}
            size="sm"
            width="14px"
          />
        )}
      </Button>

      {expanded && (
        <FocusScope autoFocus contain restoreFocus>
          <StyledList
            aria-labelledby="mobile-menu-country-selector-button"
            id="mobile-menu-country-selector-list"
            role="listbox"
          >
            {countries
              .filter(({ geo }) => geo !== sl.config.site_country_for_analytics)
              .map((country) => {
                return (
                  <CountryOption
                    closeDropdown={() => showOptions(false)}
                    country={country}
                    hideCountryName={hideCountryName}
                    key={country.geo}
                    onClick={() => doSwitch(country.geo)}
                  />
                )
              })}
          </StyledList>
        </FocusScope>
      )}
    </Box>
  )
}

CountrySelector.propTypes = {
  doSwitch: PropTypes.func,
  hideCountryName: PropTypes.bool,
}

export default withCountrySwitch(CountrySelector)
